import { ArrowRight, Chrome, Search, Sparkles, Users, Download } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { Button } from "@/components/ui/button";

const Hero = () => {
  const { t } = useLanguage();

  const scrollToInstall = () => {
    const element = document.getElementById('install');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToFeatures = () => {
    const element = document.getElementById('features');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative pt-32 pb-20 px-6 overflow-hidden">
      {/* Subtle background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-transparent pointer-events-none" />
      
      <div className="relative max-w-3xl mx-auto text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-sm text-muted-foreground mb-6 animate-fade-in">
          <Sparkles className="w-3.5 h-3.5" />
          {t('localRoute', 'badge')}
        </div>

        {/* Headline */}
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-6 animate-fade-in" style={{ animationDelay: '0.1s' }}>
          {t('localRoute', 'heroTitle_line1')}{' '}
          <span className="text-foreground">
            {t('localRoute', 'heroTitle_gradient')}
          </span>
        </h1>

        {/* Subtitle */}
        <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-10 leading-relaxed animate-fade-in" style={{ animationDelay: '0.2s' }}>
          {t('localRoute', 'heroSubtitle')}
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <Button
            size="lg"
            onClick={scrollToInstall}
            className="group gap-2"
          >
            <Chrome className="w-4 h-4" />
            {t('localRoute', 'ctaInstall')}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={scrollToFeatures}
          >
            {t('localRoute', 'ctaFeatures')}
          </Button>
        </div>

        {/* Social proof */}
        <div className="flex items-center justify-center gap-8 text-sm text-muted-foreground animate-fade-in" style={{ animationDelay: '0.4s' }}>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span>100+ active users</span>
          </div>
          <div className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            <span>200+ downloads</span>
          </div>
        </div>
      </div>

      {/* Browser Preview */}
      <div className="relative max-w-4xl mx-auto mt-16 animate-fade-in" style={{ animationDelay: '0.5s' }}>
        <div className="bg-card rounded-xl border border-border overflow-hidden shadow-lg">
          {/* Browser chrome */}
          <div className="flex items-center gap-2 px-4 py-3 bg-muted/50 border-b border-border">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
            </div>
            <span className="ml-4 text-xs text-muted-foreground">{t('localRoute', 'browserTitle')}</span>
          </div>
          
          {/* Extension UI preview */}
          <div className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-1 flex items-center gap-3 px-4 py-3 rounded-lg bg-muted border border-border">
                <Search className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground text-sm">{t('localRoute', 'searchPlaceholder')}</span>
                <span className="ml-auto text-xs text-muted-foreground bg-background px-2 py-1 rounded">Ctrl+Shift+Space</span>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {['GitHub', 'Gmail', 'Slack', 'Figma', 'Notion', 'Twitter', 'LinkedIn', 'Drive'].map((name) => (
                <div 
                  key={name} 
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 border border-border hover:border-foreground/20 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center text-sm font-medium">
                    {name[0]}
                  </div>
                  <span className="text-sm">{name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
